import React, { FC, useState } from 'react';
import { Button, Box, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { Order, ProductionValueReport } from 'models/order';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import ProductionValueReportComponent from 'components/shared/production-value-report/production-value-report.component';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { hasRestorationAction } from 'utils/restoration-price-rows';

type ProductionFinishedDialogProps = {
    order: Order;
    onClose: () => void;
    open: boolean;
    isProductionFinished: boolean;
    productionFinishedDialogChange: () => void;
    productionValueReportChange: (data: { orderId: string; productionValueReports: ProductionValueReport[] }) => void;
};

const ProductionFinishedDialog: FC<ProductionFinishedDialogProps> = ({
    order,
    isProductionFinished,
    onClose,
    open,
    productionFinishedDialogChange,
    productionValueReportChange,
}: ProductionFinishedDialogProps) => {
    const { t: translate } = useTranslation(['orders']);
    const currentUser = currentUserVar();
    const currentOrg = currentOrgVar();
    const [isValid, setIsValid] = useState(true);

    const isProductionValueReportEnabled = customerSettings.getCustomerSetting(
        currentOrg.customerSettings,
        CONSTANTS.SETTINGS_NAMES.PRODUCTION_VALUE_REPORT,
    );
    const hasAtLeastOneAction = hasRestorationAction(order?.restorations);

    const dialogTitle = () => {
        return isProductionFinished
            ? translate('orders:PRODUCTION_NOT_FINISHED_ACTION')
            : translate('orders:PRODUCTION_FINISHED_ACTION');
    };

    const handleValidationChange = (valid: boolean) => {
        setIsValid(valid);
    };

    return (
        <Box>
            <DHDialog
                maxWidth={isProductionValueReportEnabled && !isProductionFinished && hasAtLeastOneAction ? 'md' : 'sm'}
                open={open}
                onClose={onClose}
                title={dialogTitle()}
                actions={
                    <Button disabled={!isValid} variant="contained" onClick={productionFinishedDialogChange}>
                        {dialogTitle()}
                    </Button>
                }
            >
                <Box mb={1}>
                    <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
                        {isProductionFinished
                            ? translate('orders:PRODUCTION_NOT_FINISHED_NOTE')
                            : translate('orders:PRODUCTION_FINISHED_NOTE')}
                    </Alert>
                </Box>
                <Box>
                    <Alert severity="warning" sx={{ whiteSpace: 'pre-wrap' }}>
                        {isProductionFinished
                            ? translate('orders:CONFIRM_PRODUCTION_NOT_FINISHED')
                            : translate('orders:CONFIRM_PRODUCTION_FINISHED')}
                    </Alert>
                </Box>
                {isProductionValueReportEnabled && !isProductionFinished && hasAtLeastOneAction && (
                    <ProductionValueReportComponent
                        order={order}
                        isProductionFinished={true}
                        isTechnicianAdmin={currentUser.role === 'TECHNICIAN_ADMIN'}
                        onDataChange={productionValueReportChange}
                        onValidationChange={handleValidationChange}
                    />
                )}
            </DHDialog>
        </Box>
    );
};

export default ProductionFinishedDialog;
